import React from 'react';
import * as consts from 'config/consts';
import { useModal } from 'hooks/useModal';
import { useRouting } from 'hooks/useRouting';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import { validateProps } from 'components/features/target-achievement-support/pages/TargetUpdateStep1';
import {
  Account,
  AccountId,
  Target,
  TargetId,
  TargetTitle,
} from 'components/features/target-achievement-support/utils/type';
import { TargetAssociatedAlertModal } from '../modal/TargetAssociatedAlertModal';
import { WhatIsSetAccountModal } from '../modal/WhatIsSetAccountModal';
import { Errors, Error } from '../../../commons/Error';

export const getLinkToAnotherTarget = (
  targets: Target[],
  accountId: AccountId
): Target | null => {
  return (
    targets.find(
      target =>
        target.reserveAmount !== null &&
        target.excludeAmount !== null &&
        target.accountList !== null &&
        target.accountList.find(accountList =>
          accountList.account.includes(accountId)
        )
    ) ?? null
  );
};

const OverwriteAssociationOfAccountAlert = (props: {
  targetTitle: TargetTitle;
  className?: string;
}): React.ReactElement => {
  return (
    <>
      <div className="bg-white py-2.5 px-8 rounded-b-7">
        <div className="pl-9 flex flex-row items-baseline">
          <div className="icon-alert" style={{ color: '#ffcb49' }}></div>
          <div className="text-xs ml-2 text-red-550">
            別の目標「{props.targetTitle}」に登録中
          </div>
        </div>
        <div className="pl-9 text-xs mt-1">
          この目標に登録すると、すでに登録中の目標から登録が外れます。
        </div>
      </div>
    </>
  );
};

const AccountItem = ({
  account,
  targets,
  id,
  isSelected,
  toggle,
}: {
  account: Account;
  targets: Target[];
  id: TargetId;
  isSelected: boolean;
  toggle: () => void;
}): React.ReactElement => {
  const [isShowModal, { hideModal, showModal }] = useModal();

  const linkToAnotherTarget = getLinkToAnotherTarget(targets, account.account);
  const linkToAnotherTargetTitles =
    linkToAnotherTarget !== null ? [linkToAnotherTarget.title] : [];

  const isAnotherTargetSlected =
    linkToAnotherTarget && linkToAnotherTarget.id !== id;

  const onClickItem =
    isAnotherTargetSlected && !isSelected ? showModal : toggle;

  return (
    <>
      <div className="bg-white rounded-7 mt-2">
        <div
          className="bg-bluegray-50 border border-bluegray-300 rounded-7"
          onClick={onClickItem}>
          <div className="py-2.5 px-8 flex flex-row items-center cursor-pointer">
            <div
              className={`w-5 h-5 flex items-center justify-center icon-check text-white rounded-sm ${
                isSelected ? 'bg-blue-700' : 'bg-bluegray-300'
              }`}></div>
            <div className="flex flex-row items-center ml-4 text-base font-bold text-bluegray-700">
              <div
                className={`flex items-center pt-1 ${
                  !account.available ? 'text-bluegray-400' : ''
                }`}>
                {(!account.available || isAnotherTargetSlected) && (
                  <div className="icon-alert text-yellow-375 mr-1"></div>
                )}
                <div>{account.brokerName}</div>{' '}
              </div>
            </div>

            <div className="ml-auto">
              {account.available ? (
                <div className="flex flex-row items-center">
                  <div className="pt-1 text-base text-bluegray-700">
                    {account.totalAssetAmount === null
                      ? '---'
                      : commaFormat(account.totalAssetAmount, {
                          numberOfDecimals: 0,
                        })}
                  </div>
                  <div className="ml-1 pt-1 text-sm text-bluegray-700">円</div>
                </div>
              ) : (
                <div className="text-xs text-bluegray-400">集計対象外</div>
              )}
            </div>
          </div>
          {isAnotherTargetSlected && linkToAnotherTarget && (
            <>
              <OverwriteAssociationOfAccountAlert
                {...{ targetTitle: linkToAnotherTarget.title }}
              />
            </>
          )}
          {!account.available && (
            <div className="bg-white py-2.5 px-8 rounded-b-7">
              <div className="pl-9 flex flex-row items-baseline">
                <div className="icon-alert text-yellow-375"></div>
                <div className="text-xs ml-2 text-red-550">
                  集計対象外の口座は登録しても集計されません
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {linkToAnotherTargetTitles.length !== 0 ? (
        <TargetAssociatedAlertModal
          {...{
            linkToAnotherTargetTitles,
            hide: hideModal,
            isShow: isShowModal,
            onCancel: hideModal,
            onRegisterWithNewTarget: () => {
              toggle();
              hideModal();
            },
          }}
        />
      ) : (
        <></>
      )}
    </>
  );
};

const AccountList = ({
  id,
  targets,
  accounts,
  selectedAccounts,
  setSelectedAccounts,
  validateTargetAmount,
}: {
  id: TargetId;
  targets: Target[];
  accounts: Account[];
  selectedAccounts: AccountId[];
  setSelectedAccounts: (account: AccountId[]) => void;
  validateTargetAmount: validateProps;
}) => {
  const accountList = [
    {
      title: '証券口座',
      accounts: accounts.filter(
        account => account.brokerType === consts.BrokerType.証券
      ),
    },
    {
      title: '銀行口座',
      accounts: accounts.filter(
        account => account.brokerType === consts.BrokerType.銀行
      ),
    },
    {
      title: 'iDeCo・企業型確定拠出年金',
      accounts: accounts.filter(
        account => account.brokerType === consts.BrokerType.確定拠出年金
      ),
    },
    {
      title: '手入力の資産',
      accounts: accounts.filter(
        account => account.brokerType === consts.BrokerType.不明
      ),
    },
  ];

  const [
    isShowTargetAlertModal,
    { hideModal: hideTargetAlertModal, showModal: showTargetAlertModal },
  ] = useModal();

  const [
    isShowWhatIsSetAccountModal,
    {
      hideModal: hideWhatIsSetAccountModal,
      showModal: showWhatIsSetAccountModal,
    },
  ] = useModal();

  const validAcounts = accounts.filter(account => {
    return account.available === true;
  });

  const isAllSelected = validAcounts.every(account =>
    selectedAccounts.includes(account.account)
  );

  const toggleAll = (): void =>
    validAcounts.length !== selectedAccounts.length
      ? setSelectedAccounts(validAcounts.map(account => account.account))
      : setSelectedAccounts([]);

  const linkToAnotherTargetList = accounts.flatMap(account => {
    if (selectedAccounts.includes(account.account)) return [];
    const linkToAnotherTarget = getLinkToAnotherTarget(
      targets,
      account.account
    );
    if (linkToAnotherTarget === null) return [];
    return [{ account, target: linkToAnotherTarget }];
  });

  const linkToAnotherTargetTitles = linkToAnotherTargetList
    .reduce(
      ({ targetIds, targets }, { target }) => {
        if (targetIds.includes(target.id)) return { targetIds, targets };
        return {
          targetIds: [...targetIds, target.id],
          targets: [...targets, target],
        };
      },
      { targetIds: [] as Target['id'][], targets: [] as Target[] }
    )
    .targets.sort((a, b) => a.id.localeCompare(b.id))
    .map(target => target.title);

  const handleToggleAll = () => {
    if (linkToAnotherTargetList.length > 0) {
      showTargetAlertModal();
    } else {
      toggleAll();
    }
  };

  const handleToggleAccount = (account: Account) => {
    const isSelected = selectedAccounts.includes(account.account);
    const updatedSelectedAccounts = isSelected
      ? selectedAccounts.filter(accountId => accountId !== account.account)
      : [...selectedAccounts, account.account];

    setSelectedAccounts(updatedSelectedAccounts);
  };

  return (
    <>
      <div className="flex pt-4 pb-2 px-4">
        <div
          className="flex flex-row items-center gap-4 cursor-pointer"
          onClick={handleToggleAll}>
          <div
            className={`w-5 h-5 flex items-center justify-center icon-check text-white rounded-sm ${
              isAllSelected ? 'bg-blue-700' : 'bg-bluegray-300'
            }`}></div>
          <div className="text-sm text-bluegray-700">一括選択</div>
        </div>
        <div
          className="flex flex-row justify-end items-center text-bluegray-500 mr-2 ml-auto cursor-pointer"
          onClick={(event): void => {
            event.stopPropagation();
            showWhatIsSetAccountModal();
          }}>
          <div className="text-sm">口座の設定とは</div>
          <div className="ml-2 pb-1 icon-help_outline"></div>
        </div>
      </div>
      <div className="py-4 pl-12 pr-4 border-t border-bluegray-300">
        {accountList.map((accountList, i) => {
          return accountList.accounts.length === 0 ? (
            <React.Fragment key={i}></React.Fragment>
          ) : (
            <React.Fragment key={i}>
              <div
                className={`${
                  i !== 0 ? 'mt-4' : ''
                } font-bold text-bluegray-700`}>
                {accountList.title}
              </div>
              {accountList.accounts.map((account, i) => (
                <AccountItem
                  {...{
                    targets,
                    account,
                    id,
                    isSelected: selectedAccounts.includes(account.account),
                    toggle: (): void => handleToggleAccount(account),
                  }}
                  key={i}
                />
              ))}
            </React.Fragment>
          );
        })}
        {!validateTargetAmount.isValid && (
          <Errors className="mt-2">
            <Error
              error={{
                title: 'エラー',
                description: validateTargetAmount.error ?? '',
              }}
              errorToString={e => e.description}
            />
          </Errors>
        )}
      </div>
      <TargetAssociatedAlertModal
        {...{
          linkToAnotherTargetTitles,
          hide: hideTargetAlertModal,
          isShow: isShowTargetAlertModal,
          onCancel: hideTargetAlertModal,
          onRegisterWithNewTarget: () => {
            toggleAll();
            hideTargetAlertModal();
          },
        }}
      />
      <WhatIsSetAccountModal
        {...{
          hide: hideWhatIsSetAccountModal,
          isShow: isShowWhatIsSetAccountModal,
        }}
      />
    </>
  );
};

const NoAlignmentAccount = () => {
  const routing = useRouting();

  const [
    isShowWhatIsSetAccountModal,
    {
      hideModal: hideWhatIsSetAccountModal,
      showModal: showWhatIsSetAccountModal,
    },
  ] = useModal();

  return (
    <>
      <div className="flex pt-4 pb-2 px-4">
        <div className="flex flex-row items-center gap-4 cursor-pointer">
          <div className="w-5 h-5 flex items-center justify-center icon-check text-white rounded-sm bg-bluegray-300"></div>
          <div className="text-sm text-bluegray-700">一括選択</div>
        </div>
        <div
          className="flex flex-row justify-end items-center text-bluegray-500 mr-2 ml-auto cursor-pointer"
          onClick={(event): void => {
            event.stopPropagation();
            showWhatIsSetAccountModal();
          }}>
          <div className="text-sm">口座の設定とは</div>
          <div className="ml-2 pb-1 icon-help_outline"></div>
        </div>
      </div>
      <div className="py-4 pl-12 pr-4 border-t border-bluegray-300">
        <div className="text-sm">
          連携する口座がありません。
          <br />
          <span
            className="text-blue-700 cursor-pointer underline"
            onClick={() =>
              routing.pushRouting({ path: '/securityaccountslink' }, true)
            }>
            連携する口座の選択
          </span>
          から口座を連携してください。
        </div>
      </div>
      <WhatIsSetAccountModal
        {...{
          hide: hideWhatIsSetAccountModal,
          isShow: isShowWhatIsSetAccountModal,
        }}
      />
    </>
  );
};

export const SetAccount = ({
  id,
  targets,
  accounts,
  selectedAccounts,
  setSelectedAccounts,
  validateTargetAmount,
}: {
  id: TargetId;
  targets: Target[];
  accounts: Account[];
  selectedAccounts: AccountId[];
  setSelectedAccounts: (account: AccountId[]) => void;
  validateTargetAmount: validateProps;
}) => {
  return (
    <>
      <div className="mt-8 mx-2 text-lg font-bold">初期投資金額</div>
      <div className="bg-white mt-2 mx-2 rounded-lg shadow">
        {accounts.length !== 0 ? (
          <AccountList
            {...{
              id,
              targets,
              accounts,
              selectedAccounts,
              setSelectedAccounts,
              validateTargetAmount,
            }}
          />
        ) : (
          <NoAlignmentAccount />
        )}
      </div>
    </>
  );
};
